import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import { AccessTypeEnum, EntityTypeEnum } from '@api/ringil-api.v2';
import * as yup from 'yup';
import Section from '@components/obsolete/Section/Section.tsx';
import LabelsControlled from '@components/obsolete/Labels/LabelsControlled.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import { Trans } from 'react-i18next';
import { Field } from '@components/framework/formfield/Field.tsx';
import { InputDocumentType } from '@components/obsolete/Form/InputDocumentType/InputDocumentType.tsx';
import { LovDocumentTypeOutDTO } from '@api/ringil-api.v2';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';

interface LabelDefinition {
  labelId: string;
  labelValue: string;
  labelColor: string;
}

interface AttachmentInfo {
  attachmentId: string;
  fileName: string;
  labels?: LabelDefinition[];
}

interface ComponentProps {
  onComplete: () => void;
  selectedAttachments: AttachmentInfo[];
}

interface Form {
  overwriteLabels: boolean;
  labels: string[];
  overwriteDocumentType: boolean;
  documentType?: LovDocumentTypeOutDTO;
  overwriteAccessType: boolean;
  accessType?: AccessTypeEnum;
}

const validationSchema = yup.object().shape({
  labels: yup.array().of(yup.string()),
  overwriteLabels: yup.boolean(),
  overwriteDocumentType: yup.boolean(),
  documentType: yup.object().nullable(),
  overwriteAccessType: yup.boolean(),
  accessType: yup.string().nullable(),
});

export const useMAAttachment = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const MassActionAttachment: React.FC<ComponentProps> = ({ selectedAttachments, onComplete }) => {
    const { tr } = useTranslationLgs();
    const { attachmentUpdateMassAction } = useContext(ApiContext);

    const form = useForm<Form>(
      validationSchema,
      {
        labels: [],
        overwriteLabels: false,
        overwriteDocumentType: false,
        documentType: null,
        overwriteAccessType: false,
        accessType: null,
      },
      d => {
        save(d);
      },
      true,
      false,
    );

    const save = (data: Form) => {
      setLoading(true);
      attachmentUpdateMassAction(
        {
          attachmentIds: selectedAttachments.map(t => t.attachmentId),
          overwriteLabels: data.overwriteLabels,
          labelIds: data.labels,
          overwriteDocumentType: data.overwriteDocumentType,
          documentType: data.documentType?.documentType,
          overwriteAccessType: data.overwriteAccessType,
          accessType: data.accessType,
        },
        () => {
          setLoading(false);
          setVisible(false);
          onComplete();
        },
        null,
        {
          onError: (_error, showStandardError) => {
            setLoading(false);
            showStandardError();
          },
        },
      );
    };

    const names = proxiedPropertiesOf<Form>();

    return (
      <>
        <RingilSidebar
          visible={visible}
          loading={loading}
          heading={tr(`MAAttachment.massAction`, `Mass action for attachments`)}
          onBack={() => {
            setLoading(false);
            setVisible(false);
          }}
          onContinue={() => {
            form.validateAndSend();
          }}
        >
          <div className="flex flex-col gap-4">
            <COInlineMessage icon={<SvgGrayInfo />} variant={'info'}>
              <Trans i18nKey={'MAAttachment.selectedAttachmentsInfo'} values={{ count: selectedAttachments.length }} />
            </COInlineMessage>

            <Section
              label={tr(`MAAttachment.overrideLabels`, `Override labels`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.labels, []);
                form.setFieldValue(names.overwriteLabels, v);
              }}
            >
              <LabelsControlled
                entityType={EntityTypeEnum.ATTACHMENTS}
                value={form.values.labels}
                onChange={v => {
                  form.setFieldValue(names.labels, v);
                }}
              />
            </Section>

            <Section
              label={tr(`MAAttachment.overwriteDocumentType`, `Override document type`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.documentType, null);
                form.setFieldValue(names.overwriteDocumentType, v);
              }}
            >
              <Field label={tr('UploadAttachmentDialog.documentType', 'Document type')} required>
                <InputDocumentType
                  documentType={form.values.documentType?.documentType}
                  onChange={v => form.setFieldValue(names.documentType, v)}
                />
              </Field>
            </Section>

            <Section
              label={tr(`MAAttachment.overwriteAccessType`, `Override access type`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.accessType, null);
                form.setFieldValue(names.overwriteAccessType, v);
              }}
            >
              <Field
                label={tr('UploadAttachmentDialog.accessType', 'Visibility')}
                required
                helpText={tr('UploadAttachmentDialog.sharedDocumentInfo', 'Sdílený dokument je viditelný také vašim partnerům')}
              >
                <InputSelect
                  model={[
                    { code: AccessTypeEnum.INTERNAL, label: tr('UploadAttachmentDialog.internal', 'Internal') },
                    { code: AccessTypeEnum.ALL, label: tr('UploadAttachmentDialog.shared', 'Shared') },
                  ]}
                  selectedItemCode={form.values.accessType}
                  onSelect={(code) => form.setFieldValue(names.accessType, code as AccessTypeEnum)}
                />
              </Field>
            </Section>
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { MassActionAttachment, setVisible };
};
